import * as yup from "yup";

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const emailField = yup
  .string()
  .required("Enter your email address")
  .matches(EMAIL_REGEX, "Enter a valid email address");

/**
 * Per-requirement password rules. Mirrors deex-new-pwa. Deliberately not a
 * single regex — the old one's character class only allowed @$!%*?&, so valid
 * passwords like "Password_1#" were wrongly rejected.
 */
export const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (v: string) => v.length >= 8 },
  { label: "One uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
  { label: "One lowercase letter", test: (v: string) => /[a-z]/.test(v) },
  { label: "One number", test: (v: string) => /\d/.test(v) },
  {
    label: "One special character",
    test: (v: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(v),
  },
];

export const passwordField = yup
  .string()
  .required("Enter a password")
  .test(
    "password-rules",
    "Password must be 8+ characters and include an uppercase letter, a lowercase letter, a number and a special character",
    (value) => PASSWORD_RULES.every((rule) => rule.test(value ?? ""))
  );

export const confirmPasswordField = (ref: string = "password") =>
  yup
    .string()
    .required("Re-enter your password")
    .oneOf([yup.ref(ref)], "Passwords do not match");

export const otpField = yup
  .string()
  .required("Enter the 6-digit code")
  .length(6, "Enter all 6 digits");

// --- Per-form schemas -------------------------------------------------------

export const loginSchema = yup.object({
  email: emailField,
  password: yup.string().required("Enter your password"),
});

export const signUpSchema = yup.object({
  firstName: yup.string().required("Enter your first name"),
  lastName: yup.string().required("Enter your last name"),
  email: emailField,
  country: yup.string().required("Select your country of residence"),
  phoneNumber: yup.string().required("Enter your phone number"),
  password: passwordField,
  confirmPassword: confirmPasswordField(),
});

export const forgotPasswordSchema = yup.object({
  email: emailField,
});

export const resetPasswordSchema = yup.object({
  otp: otpField,
  password: passwordField,
  confirmPassword: confirmPasswordField(),
});

export const otpSchema = yup.object({
  otp: otpField,
});

export const businessProfileSchema = yup.object({
  name: yup.string().required("Enter your business name"),
  industry: yup.string().required("Select your business industry"),
  email: emailField,
  address: yup.string().required("Enter your business address"),
  size: yup.string().required("Select your business size"),
  country: yup.string().required("Select your business country"),
  description: yup.string().required("Describe your business"),
});

/**
 * Editing an existing business is much narrower than creating one: PATCH
 * /api/businesses/:id only accepts `logo` and these three `details` fields.
 * Name, email, industry and country are set at creation and only support can
 * change them, so they are displayed read-only rather than validated here.
 */
export const businessDetailsEditSchema = yup.object({
  address: yup.string().required("Enter your business address"),
  size: yup.string().required("Select your business size"),
  description: yup.string().required("Describe your business"),
});

export const branchProfileSchema = yup.object({
  name: yup.string().required("Enter a name for this branch"),
  address: yup.string().required("Enter the branch address"),
  size: yup.string(),
  description: yup.string(),
  managerEmail: yup
    .string()
    .notRequired()
    .test(
      "valid-email",
      "Enter a valid email address",
      (v) => !v || EMAIL_REGEX.test(v)
    ),
});
